import type { OrderLineItem, OrderNode } from '../graphql/queries/orders';
import {
  detectOrderType,
  formatOrderTotal,
  getOrderLineItemDisplayName,
  metaArrayToRecord,
  type LineItemOrderType,
} from './orderLineItemMeta';

export type BookingServiceType = 'hotel' | 'tour' | 'transfer' | 'package';

export interface BookingServiceItem {
  type: BookingServiceType;
  subOrderNo: string;
  packageName: string;
  travelDate: string;
  amount: string;
  rawAmount?: string;
  order: OrderNode;
  lineIndex: number;
}

export interface BookingOrderSummary {
  order: OrderNode;
  orderNo: string;
  customerName: string;
  bookingDate: string;
  travelStartDate: string;
  status: string;
  amount: string;
  paymentStatus: string;
  types: BookingServiceType[];
  services: BookingServiceItem[];
}

const TYPE_PREFIX: Record<BookingServiceType, string> = {
  tour: 'T',
  hotel: 'H',
  transfer: 'R',
  package: 'P',
};

const TYPE_BADGE: Record<BookingServiceType, { color: string; label: string }> = {
  hotel: { color: 'primary', label: 'Hotel' },
  tour: { color: 'warning', label: 'Tour' },
  transfer: { color: 'info', label: 'Transfer' },
  package: { color: 'secondary', label: 'Package' },
};

export function getTypeBadge(type: BookingServiceType) {
  return TYPE_BADGE[type];
}

export function getCustomerName(order: OrderNode): string {
  const first = order.billing?.firstName;
  const last = order.billing?.lastName;
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return order.billing?.email || order.customer?.email || '—';
}

export function formatBookingDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatShortDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const parsed = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getPackageInfoDate(meta: Record<string, string>): string {
  if (!meta.packageInfo) return '';
  try {
    const pkg = JSON.parse(meta.packageInfo) as { date?: unknown };
    return typeof pkg?.date === 'string' ? pkg.date : '';
  } catch {
    return '';
  }
}

export function normalizeOrderStatus(status?: string | null): string {
  return (status || '').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
}

export function getStatusColor(status: string): string {
  const map: Record<string, string> = {
    PROCESSING: 'success',
    COMPLETED: 'success',
    CONFIRMED: 'success',
    ON_HOLD: 'warning',
    PENDING: 'secondary',
    CANCELLED: 'danger',
    CANCEL_REQUEST: 'warning',
    REFUNDED: 'info',
    FAILED: 'danger',
  };
  const upper = normalizeOrderStatus(status);
  return map[upper] || 'secondary';
}

export const BOOKING_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCEL_REQUEST', label: 'Cancel request' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'FAILED', label: 'Failed' },
];

export function formatStatusLabel(status?: string | null): string {
  const normalized = normalizeOrderStatus(status);
  const match = BOOKING_STATUS_OPTIONS.find((option) => option.value === normalized);
  if (match) return match.label;
  if (!normalized) return '—';
  return normalized
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export const NGK_PAYMENT_STATUS_META_KEY = '_ngk_payment_status';

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'Paid', label: 'Paid' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Refunded', label: 'Refunded' },
  { value: 'Unpaid', label: 'Unpaid' },
] as const;

export type PaymentStatusValue = (typeof PAYMENT_STATUS_OPTIONS)[number]['value'];

export function normalizePaymentStatus(value?: string | null): PaymentStatusValue | '' {
  const normalized = (value || '').trim();
  if (!normalized) return '';
  const match = PAYMENT_STATUS_OPTIONS.find(
    (option) => option.value.toLowerCase() === normalized.toLowerCase()
  );
  return match?.value ?? '';
}

export function getPaymentStatus(status: string | undefined): string {
  const upper = (status || '').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (['COMPLETED', 'PROCESSING'].includes(upper)) return 'Paid';
  if (['REFUNDED'].includes(upper)) return 'Refunded';
  if (['CANCELLED', 'FAILED'].includes(upper)) return 'Unpaid';
  if (['ON_HOLD', 'PENDING'].includes(upper)) return 'Pending';
  return 'Pending';
}

export function resolvePaymentStatus(
  order: Pick<OrderNode, 'status' | 'metaData'>
): PaymentStatusValue {
  const meta = metaArrayToRecord(order.metaData);
  const manual = normalizePaymentStatus(meta[NGK_PAYMENT_STATUS_META_KEY]);
  if (manual) return manual;
  return normalizePaymentStatus(getPaymentStatus(order.status)) || 'Pending';
}

export function paymentStatusToIsPaid(paymentStatus: PaymentStatusValue): boolean {
  return paymentStatus === 'Paid';
}

export function getPaymentBadgeColor(payment: string): string {
  if (payment === 'Paid') return 'primary';
  if (payment === 'Refunded') return 'info';
  if (payment === 'Unpaid') return 'danger';
  return 'secondary';
}

function classifyLineItem(item: OrderLineItem): BookingServiceType {
  const meta = metaArrayToRecord(item.metaData);
  const productName = item.product?.node?.name || '';
  const detected = detectOrderType(meta, productName);
  if (
    detected === 'hotel' ||
    detected === 'tour' ||
    detected === 'transfer' ||
    detected === 'package'
  ) {
    return detected;
  }

  const name = productName.toLowerCase();
  if (
    name.includes('hotel') ||
    name.includes('ngk hotel') ||
    meta._ngk_hotel_name ||
    meta._ngk_hotel_booking === 'yes'
  ) {
    return 'hotel';
  }
  if (
    name.includes('transfer') ||
    name.includes('airport') ||
    meta._tat_vehicle ||
    meta._tat_pickup_name ||
    meta._tat_pickup
  ) {
    return 'transfer';
  }
  if (meta.packageInfo || name.includes('package')) {
    return 'package';
  }
  if (
    name.includes('tour') ||
    name.includes('safari') ||
    name.includes('excursion') ||
    name.includes('experience') ||
    name.includes('trip') ||
    name.includes('placeholder') ||
    meta['Tour Title'] ||
    meta['Tour ID']
  ) {
    return 'tour';
  }
  // Unclassified line items are packages (aligned with Booking Calendar), not tours
  return 'package';
}

function getPackageName(type: BookingServiceType, item: OrderLineItem): string {
  const meta = metaArrayToRecord(item.metaData);
  const displayName = getOrderLineItemDisplayName(item.product?.node?.name, item.metaData);
  if (type === 'hotel') {
    return meta._ngk_hotel_name || meta['Tour Title'] || displayName || 'Hotel booking';
  }
  if (type === 'transfer') {
    const pickup = meta._tat_pickup_name || meta._tat_pickup || '';
    const dropoff = meta._tat_dropoff_name || meta._tat_dropoff || '';
    if (pickup || dropoff) {
      return [pickup || 'Pickup', dropoff || 'Drop-off'].join(' ⇄ ');
    }
    return meta._tat_package_name || displayName || 'Transfer';
  }
  if (type === 'package') {
    return meta._tat_package_name || meta['Tour Title'] || displayName || 'Package';
  }
  return meta['Tour Title'] || displayName || 'Tour';
}

function getTravelDate(type: BookingServiceType, item: OrderLineItem, orderDate?: string): string {
  const meta = metaArrayToRecord(item.metaData);
  if (type === 'hotel') {
    const checkIn = meta._ngk_check_in || meta['Booking Date'];
    const checkOut = meta._ngk_check_out;
    if (checkIn && checkOut) {
      return `${formatShortDate(checkIn)} → ${formatShortDate(checkOut)}`;
    }
    if (checkIn) return formatShortDate(checkIn);
  }
  if (type === 'transfer') {
    const dep = meta.tat_fields_departure_date || meta._tat_departure_date;
    if (dep) return formatShortDate(dep);
    const pickup = meta._tat_pickup_name || meta._tat_pickup;
    const dropoff = meta._tat_dropoff_name || meta._tat_dropoff;
    if (pickup || dropoff) {
      return [pickup, dropoff].filter(Boolean).join(' ⇄ ');
    }
  }
  if (type === 'tour' || type === 'package') {
    const tourDate =
      (type === 'package' ? getPackageInfoDate(meta) : '') ||
      meta['Booking Date'] ||
      meta.booking_date;
    if (tourDate) return formatShortDate(tourDate);
  }
  return formatBookingDate(orderDate);
}

function getTravelStartSortValue(type: BookingServiceType, item: OrderLineItem, orderDate?: string): string {
  const meta = metaArrayToRecord(item.metaData);
  if (type === 'hotel') return meta._ngk_check_in || meta['Booking Date'] || orderDate || '';
  if (type === 'transfer') {
    return meta.tat_fields_departure_date || meta._tat_departure_date || orderDate || '';
  }
  if (type === 'package') {
    return getPackageInfoDate(meta) || meta['Booking Date'] || meta.booking_date || orderDate || '';
  }
  return meta['Booking Date'] || meta.booking_date || orderDate || '';
}

export function sortOrdersNewestFirst(a: OrderNode, b: OrderNode): number {
  const parseTime = (value?: string) => {
    if (!value) return -1;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : -1;
  };
  const parseOrderNumber = (value?: string) => {
    if (!value) return 0;
    const num = Number(String(value).replace(/[^\d]/g, ''));
    return Number.isFinite(num) ? num : 0;
  };
  const aTime = parseTime(a.date);
  const bTime = parseTime(b.date);
  if (aTime !== bTime) return bTime - aTime;
  const aOrder = parseOrderNumber(a.orderNumber);
  const bOrder = parseOrderNumber(b.orderNumber);
  if (aOrder !== bOrder) return bOrder - aOrder;
  return (b.databaseId || 0) - (a.databaseId || 0);
}

export function buildOrderSummary(order: OrderNode): BookingOrderSummary {
  const lineItems = order.lineItems?.nodes ?? [];
  const counters: Record<BookingServiceType, number> = {
    tour: 0,
    hotel: 0,
    transfer: 0,
    package: 0,
  };
  const services: BookingServiceItem[] = [];
  const typeSet = new Set<BookingServiceType>();
  let earliestTravel = '';

  lineItems.forEach((item, lineIndex) => {
    const type = classifyLineItem(item);
    counters[type] += 1;
    typeSet.add(type);
    const subOrderNo = `#${order.orderNumber ?? order.databaseId}-${TYPE_PREFIX[type]}${counters[type]}`;
    const travelSort = getTravelStartSortValue(type, item, order.date);
    if (
      travelSort &&
      formatShortDate(travelSort) !== '—' &&
      (!earliestTravel || travelSort < earliestTravel)
    ) {
      earliestTravel = travelSort;
    }
    services.push({
      type,
      subOrderNo,
      packageName: getPackageName(type, item),
      travelDate: getTravelDate(type, item, order.date),
      amount: formatOrderTotal(item.total, order.currency),
      rawAmount: item.total,
      order,
      lineIndex,
    });
  });

  return {
    order,
    orderNo: `#${order.orderNumber ?? order.databaseId}`,
    customerName: getCustomerName(order),
    bookingDate: formatBookingDate(order.date),
    travelStartDate: earliestTravel ? formatShortDate(earliestTravel) : formatBookingDate(order.date),
    status: order.status || '—',
    amount: formatOrderTotal(order.total, order.currency),
    paymentStatus: resolvePaymentStatus(order),
    types: Array.from(typeSet),
    services,
  };
}

export function buildAllBookingSummaries(orders: OrderNode[]): BookingOrderSummary[] {
  return [...orders]
    .sort(sortOrdersNewestFirst)
    .map(buildOrderSummary)
    .filter((s) => s.services.length > 0);
}

export function flattenServicesByType(
  orders: OrderNode[],
  type: BookingServiceType
): Array<BookingServiceItem & { summary: BookingOrderSummary }> {
  const rows: Array<BookingServiceItem & { summary: BookingOrderSummary }> = [];
  buildAllBookingSummaries(orders).forEach((summary) => {
    summary.services
      .filter((s) => s.type === type)
      .forEach((s) => rows.push({ ...s, summary }));
  });
  return rows;
}

export function getTransferPackageLabel(item: OrderLineItem): string {
  const meta = metaArrayToRecord(item.metaData);
  return meta._tat_package_name || meta._tat_vehicle || item.product?.node?.name || 'Transfer';
}

export function getTransferRouteLabel(item: OrderLineItem): string {
  const meta = metaArrayToRecord(item.metaData);
  const pickup = meta._tat_pickup_name || meta._tat_pickup || '';
  const dropoff = meta._tat_dropoff_name || meta._tat_dropoff || '';
  if (pickup || dropoff) return [pickup || '—', dropoff || '—'].join(' ⇄ ');
  return getTravelDate('transfer', item);
}

/** Re-export for list pages */
export { formatOrderTotal };
export type { LineItemOrderType };
